import type { AxiosRequestConfig } from 'axios';
import type { ApiSuccess, Paginated, PaginationMeta } from '@/types/api';
import { apiClient } from './client';

/**
 * Thin, typed wrappers over the Axios instance. They unwrap the success envelope
 * so callers receive `data` directly. Errors are already normalised to
 * {@link import('./errors').ApiClientError} by the response interceptor.
 */

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.get<ApiSuccess<T>>(url, config);
  return res.data.data;
}

export async function post<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.post<ApiSuccess<T>>(url, body, config);
  return res.data.data;
}

export async function put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.put<ApiSuccess<T>>(url, body, config);
  return res.data.data;
}

export async function patch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.patch<ApiSuccess<T>>(url, body, config);
  return res.data.data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.delete<ApiSuccess<T>>(url, config);
  return res.data.data;
}

/** GET a paginated collection, returning both items and pagination meta. */
export async function getPaginated<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<Paginated<T>> {
  const res = await apiClient.get<ApiSuccess<T[]>>(url, config);
  return {
    items: res.data.data,
    meta: (res.data.meta ?? {
      page: 1,
      limit: res.data.data.length,
      total: res.data.data.length,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    }) as unknown as PaginationMeta,
  };
}

export const http = { get, post, put, patch, del, getPaginated };
